import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import { useT } from "../i18n/LocaleProvider";
import { type Locale, translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// Sweep §4 / 0199–0200. WHAT THE CREW WAS TOLD, per event.
//
// A message is written once and never edited (the database refuses it); a
// correction is a second message. Who it reached is fixed by the database at
// the moment it is sent, and "opened" means the worker opened the shift in the
// app — the email only says there is a message.
//
// REMINDERS ARE A BUTTON. Nothing in this product runs by itself until the owner
// rules who may act when nobody is signed in, so the panel says how many shifts
// are due and the button queues them. Queued is not sent: the notification
// screen's send does that, and the sentence under the button says so.
// ---------------------------------------------------------------------------

type Reader = { name: string; read_at: string | null };

type Message = {
  id: string;
  body: string;
  sent_at: string;
  sent_by_you: boolean;
  crew_label: string | null;
  crew_role?: string | null;
  crew_zone?: string | null;
  recipients: number;
  opened: number;
  readers: Reader[];
};

type Crew = { id: string; label: string; role_name?: string; zone?: string | null; starts_at: string };

// The API joins "role — zone" in English; the role is a word the reader's language may have.
function crewText(locale: Locale, label: string | null, role?: string | null, zone?: string | null): string | null {
  return role ? [translateLabel(locale, role), zone].filter(Boolean).join(" — ") : label;
}

type Board = {
  messages: Message[];
  crews: Crew[];
  reminders: { hours_before: number | null; due: number };
};

const UNREACHABLE = "We could not reach Verbunk. Check your connection and try again.";

export function EventMessages() {
  const { apiBase, me } = useSession();
  const { t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const eventId = useParams()["id"] ?? "";
  const [board, setBoard] = useState<Board | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await send<Partial<Board> & { error?: { message?: string } }>(console_.eventMessages(apiBase, eventId));
      if (res.status === 200 && Array.isArray(res.body.messages)) {
        setBoard(res.body as Board);
        setFailed(null);
      } else {
        // NULL, NOT AN EMPTY LIST: "nobody has been told anything" and "this could
        // not be read" are different facts.
        setBoard(null);
        setFailed(refusal(res.body.error) ?? t("The messages for this event could not be read."));
      }
    } catch {
      setBoard(null);
      setFailed(t(UNREACHABLE));
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="stack">
      <div className="panel-head">
        <h1>{t("Messages to the crew")}</h1>
      </div>
      {failed ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not available")} />
          <span>{failed}</span>
        </div>
      ) : null}
      {/* Tour T-60 (owner): compose leads; the reminders and the sent log are one click away. */}
      {board ? (
        <Tabs
          label={t("Messages to the crew")}
          tabs={[
            { id: "write", label: t("Write"), content: <Compose crews={board.crews} eventId={eventId} orgZone={orgZone} onSent={load} /> },
            { id: "reminders", label: t("Shift reminders"), content: <Reminders board={board} eventId={eventId} onQueued={load} /> },
            {
              id: "history",
              label: t("Sent"),
              content:
                board.messages.length === 0 ? (
                  <section className="panel">
                    <p className="muted">{t("Nothing has been sent to this event's crew.")}</p>
                  </section>
                ) : (
                  <section className="panel">
                    <ul className="plain-list">
                      {board.messages.map((m) => (
                        <MessageRow key={m.id} message={m} orgZone={orgZone} />
                      ))}
                    </ul>
                  </section>
                ),
            },
          ]}
        />
      ) : null}
    </div>
  );
}

function Reminders({ board, eventId, onQueued }: { board: Board; eventId: string; onQueued: () => Promise<void> }) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "live" | "attention"; text: string } | null>(null);
  const { hours_before: hours, due } = board.reminders;

  async function queue() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ queued?: number; error?: { message?: string } }>(console_.queueReminders(apiBase, eventId));
      if (res.status !== 200) {
        setNotice({ tone: "attention", text: refusal(res.body.error) ?? t("The reminders could not be queued.") });
        return;
      }
      const n = res.body.queued ?? 0;
      setNotice({
        tone: "live",
        text: t("{n} reminder(s) queued. They go out the next time notifications are sent.", { n }),
      });
      await onQueued();
    } catch {
      setNotice({ tone: "attention", text: t(UNREACHABLE) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack">
      <h2>{t("Shift reminders")}</h2>
      {hours === null ? (
        <p className="muted">
          {t("Reminders are off for this organisation. An owner or administrator can turn them on in the organisation settings.")}
        </p>
      ) : (
        <>
          <p className="muted">
            {t("Workers are reminded {n} hour(s) before a booked shift, once. Standby is not reminded. Nothing sends these by itself yet: queue them here when they are due.", { n: hours })}
          </p>
          {due === 0 ? (
            <p>{t("No shift on this event is due a reminder.")}</p>
          ) : (
            <div className="row">
              <StatusChip tone="attention" label={t("{n} due", { n: due })} />
              <button type="button" className="btn" disabled={busy} onClick={() => void queue()}>
                {busy ? t("Queueing…") : t("Queue {n} reminder(s)", { n: due })}
              </button>
            </div>
          )}
        </>
      )}
      {notice ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone={notice.tone} label={notice.tone === "live" ? t("Queued") : t("Not queued")} />
          <span>{notice.text}</span>
        </div>
      ) : null}
    </section>
  );
}

function Compose({
  crews,
  eventId,
  orgZone,
  onSent,
}: {
  crews: Crew[];
  eventId: string;
  orgZone: string | null;
  onSent: () => Promise<void>;
}) {
  const { apiBase } = useSession();
  const { t, refusal, locale } = useT();
  const [crew, setCrew] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(
        console_.sendCrewMessage(apiBase, eventId, { body, requirement_id: crew || null }),
      );
      if (res.status !== 201) {
        setNotice(refusal(res.body.error) ?? t("That message could not be sent."));
        return;
      }
      setBody("");
      await onSent();
    } catch {
      setNotice(t(UNREACHABLE));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="panel stack"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h2>{t("Write to the crew")}</h2>
      <p className="muted">{t("Once sent it cannot be changed; send a correction as a new message.")}</p>
      <div className="field">
        <label htmlFor="message-crew">{t("Who")}</label>
        <select id="message-crew" value={crew} onChange={(e) => setCrew(e.target.value)}>
          <option value="">{t("The whole crew of this event")}</option>
          {crews.map((c) => (
            <option key={c.id} value={c.id}>
              {crewText(locale, c.label, c.role_name, c.zone)} · {whenIn(c.starts_at, orgZone)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="message-body">{t("Message")}</label>
        <textarea
          id="message-body"
          required
          maxLength={1000}
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <p className="field-hint">{t("Tell people what to do. Nothing about anybody's health.")}</p>
      </div>
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not sent")} />
          <span>{notice}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="submit" className="btn" disabled={busy || body.trim() === ""}>
          {busy ? t("Sending…") : t("Send to the crew")}
        </button>
      </div>
      <details className="role-group">
        <summary>{t("How it reaches people")}</summary>
        <p className="muted">
          {t("It reaches everybody on the crew now — accepted, briefed or checked in, standby included — in the app, with an email saying there is a message. Once sent it cannot be changed; send a correction as a new message.")}
        </p>
      </details>
    </form>
  );
}

function MessageRow({ message: m, orgZone }: { message: Message; orgZone: string | null }) {
  const { t, locale } = useT();
  const everybody = m.opened === m.recipients;
  return (
    <li className="stack">
      <div className="row">
        <StatusChip tone={everybody ? "live" : "attention"} label={t("{opened} of {n} opened", { opened: m.opened, n: m.recipients })} />
        <strong>{crewText(locale, m.crew_label, m.crew_role, m.crew_zone) ?? t("Whole crew")}</strong>
        <span className="muted">
          {whenIn(m.sent_at, orgZone)} · {m.sent_by_you ? t("sent by you") : t("sent by a colleague")}
        </span>
      </div>
      <p>{m.body}</p>
      <details>
        <summary>{t("Who has opened it")}</summary>
        <ul className="plain-list">
          {m.readers.map((r, i) => (
            <li key={i}>
              {r.name} — <span className="muted">{r.read_at ? t("opened {when}", { when: whenIn(r.read_at, orgZone) }) : t("not opened yet")}</span>
            </li>
          ))}
        </ul>
      </details>
    </li>
  );
}
